import React from 'react'
import { Box, Container, Typography, Link, IconButton } from '@mui/material'
import { Facebook, Twitter, LinkedIn, Instagram, YouTube } from '@mui/icons-material'
import { useTranslations } from 'next-intl'

interface FooterProps {
    currentYear: number
}

const Footer: React.FC<FooterProps> = ({ currentYear }) => {
    const t = useTranslations('footer')
    return (
        <Box
            component="footer"
            sx={{
                bgcolor: '#f5f7fa',
                color: '#6b7280',
                pt: 4,
                pb: 3,
                fontSize: '13px',
                fontFamily: 'Arial, sans-serif'
            }}
        >
            <Container maxWidth="md" sx={{ px: 2 }}>
                {/* Footer Links */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 3
                    }}
                >
                    <Link 
                        href="/aboutUs.en.html"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('aboutUs')}
                    </Link>
                    <Link 
                        href="/faqs.en.html"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('faq')}
                    </Link>
                    <Link 
                        href="/contactUs.en.html"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('contactUs')}
                    </Link>
                    <Link 
                        href="/flyin-wallet.en.html"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('flyinWallet')}
                    </Link>
                    <Link 
                        href="/franchise/joinUs.en.html"
                        target="_blank"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('flyinFranchise')}
                    </Link>
                    <Link 
                        href="https://extranet.flyin.com/#/hotels/suppliers/edit"
                        target="_blank"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('addYourProperty')}
                    </Link>
                    <Link 
                        href="https://careers.flyin.com/"
                        target="_blank"
                        sx={{ 
                            color: '#6b7280',
                            fontSize: 14,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('careers')}
                    </Link>
                </Box>

                {/* App Store Badges */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 3
                    }}
                >
                    <Link 
                        href="https://play.google.com/store/apps/details?id=com.flyin.bookings"
                        target="_blank"
                    >
                        <Box
                            component="img"
                            src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                            alt="Get it on Google Play"
                            sx={{ height: 45, borderRadius: 1 }}
                        />
                    </Link>
                    <Link 
                        href="https://itunes.apple.com/us/app/flyin.com-tyran-w-fnadq/id1106268318"
                        target="_blank"
                    >
                        <Box
                            component="img"
                            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                            alt="Download on the App Store"
                            sx={{ height: 45, borderRadius: 1 }}
                        />
                    </Link>
                </Box>

                {/* Social Icons */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        mb: 3
                    }}
                >
                    <IconButton
                        href="https://www.facebook.com/flyin.com"
                        target="_blank"
                        sx={{
                            bgcolor: '#3b5998',
                            color: 'white',
                            width: 40,
                            height: 40,
                            '&:hover': { bgcolor: '#2d4373' }
                        }}
                    >
                        <Facebook sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                        href="https://twitter.com/flyincom"
                        target="_blank"
                        sx={{
                            bgcolor: '#1da1f2',
                            color: 'white',
                            width: 40,
                            height: 40,
                            '&:hover': { bgcolor: '#1a91da' }
                        }}
                    >
                        <Twitter sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                        href="https://www.linkedin.com/company/flyin-com"
                        target="_blank"
                        sx={{
                            bgcolor: '#0077b5',
                            color: 'white',
                            width: 40,
                            height: 40,
                            '&:hover': { bgcolor: '#005885' }
                        }}
                    >
                        <LinkedIn sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                        href="https://www.instagram.com/flyincom/"
                        target="_blank"
                        sx={{
                            bgcolor: '#833ab4',
                            color: 'white',
                            width: 40,
                            height: 40,
                            background: 'linear-gradient(45deg, #833ab4, #fd1d1d)',
                            '&:hover': { 
                                background: 'linear-gradient(45deg, #6a2c91, #e01515)'
                            }
                        }}
                    >
                        <Instagram sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                        href="https://www.youtube.com/user/FlyinVideos"
                        target="_blank"
                        sx={{
                            bgcolor: '#ff0000',
                            color: 'white',
                            width: 40,
                            height: 40,
                            '&:hover': { bgcolor: '#cc0000' }
                        }}
                    >
                        <YouTube sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* License Numbers */}
                <Box sx={{ mb: 3 }}>
                    <Typography 
                        sx={{ 
                            fontSize: 12,
                            color: '#6b7280',
                            textAlign: 'center',
                            mb: 0.5
                        }}
                    >
                        {t('travelLicenseNumber')}: 73100559
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: 12,
                            color: '#6b7280',
                            textAlign: 'center',
                            mb: 0.5
                        }}
                    >
                        {t('licenseCategory')}: {t('travelAndTourismAgency')}
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: 12,
                            color: '#6b7280',
                            textAlign: 'center'
                        }}
                    >
                        {t('commercialRegistrationNumber')}: 1010253765
                    </Typography>
                </Box>

                {/* Copyright */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Link 
                        href="https://ui.cltp.co/flyin-static/docs/flyin-VAT-cert.pdf"
                        target="_blank"
                    >
                        <Box
                            component="img"
                            src="https://ui.cltp.co/flyin-static/img/vat-cert-logo.png"
                            alt="VAT"
                            sx={{ width: 28, height: 28 }}
                        />
                    </Link>
                    <Typography 
                        sx={{ 
                            fontSize: 12,
                            color: '#6b7280',
                            textAlign: 'center'
                        }}
                    >
                        {t('copyright', { year: currentYear })}
                    </Typography>
                </Box>
            </Container>
        </Box>
    )
}

export { Footer }
export type { FooterProps }